import clsx from 'clsx'
import { rem } from 'polished'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { mediaQueryUp } from 'components/utils/breakpoint'
import { INTL_LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import { getWeekdayNames, intlDateFormatter } from 'lib/datetime/dateUtils'
import { getCalendarWeekStart } from 'lib/datetime/calendarUtils'
import noop from 'lib/function/noop'

import type {
  CalendarDate,
  CalendarDay,
  CalendarDaySlot,
  CalendarMonth,
  CalendarProps,
  CalendarWeekday,
} from './Calendar.types'
import CalendarDayCell from './CalendarDayCell'
import CalendarMonthNav from './CalendarMonthNav'
import { addCalendarDays, clampCalendarDate, toMonthKey } from './calendarDate'
import { buildCalendarMonths } from './calendarGrid'
import { resolveDayState, selectDay } from './calendarSelection'
import { buildSpanLayout, getWeekKey } from './calendarSpans'
import { calendarTokens } from './calendarTokens'
import { useMonthWindow } from './useMonthWindow'

const DAYS_IN_WEEK = 7
const WEEKDAY_ORDER = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

/**
 * Named container so a consumer's day cell can respond to how much room the
 * grid actually has, rather than to the window. A chip that fits on a laptop
 * has no business rendering in a 45px cell.
 */
const Root = styled.div`
  ${calendarTokens}
  container-type: inline-size;
  container-name: lux-calendar;
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: 100%;
  background: var(--lux-cal-surface);
  color: var(--lux-cal-text);
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(14)};
  padding: ${rem(16)} ${rem(18)} ${rem(12)};
`

const Title = styled.span`
  font-size: ${rem(19)};
  font-weight: 700;
  letter-spacing: -0.01em;
`

const WeekdayRow = styled.div`
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(${DAYS_IN_WEEK}, minmax(0, 1fr));
  padding: ${rem(2)} ${rem(10)} ${rem(10)};
  font-size: ${rem(13)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(2)} ${rem(18)} ${rem(10)};
  }
`

const MonthLabel = styled.div`
  padding: ${rem(4)} ${rem(10)} ${rem(12)};
  font-size: ${rem(17)};
  font-weight: 700;
  letter-spacing: -0.01em;

  ${mediaQueryUp.tablet} {
    padding: ${rem(4)} ${rem(18)} ${rem(12)};
  }
`

const Grid = styled.div`
  min-width: 0;
  margin: 0 ${rem(10)};

  ${mediaQueryUp.tablet} {
    margin: 0 ${rem(18)};
    border-top: 1px solid var(--lux-cal-border);
    border-inline-start: 1px solid var(--lux-cal-border);
  }
`

const Week = styled.div`
  position: relative;
  min-width: 0;
`

const Cells = styled.div`
  display: grid;
  min-width: 0;
  /* minmax(0) so a cell can shrink below its content and ellipsis instead of
     spilling into the next day. */
  grid-template-columns: repeat(${DAYS_IN_WEEK}, minmax(0, 1fr));
`

const Lanes = styled.div`
  position: absolute;
  inset-inline: 0;
  top: ${rem(27)};
  pointer-events: none;

  ${mediaQueryUp.tablet} {
    top: ${rem(31)};
  }
`

const Footnote = styled.div`
  padding: ${rem(14)} ${rem(18)} ${rem(16)};
  font-size: ${rem(12.5)};
  color: var(--lux-cal-muted);
`

const Banner = styled.div`
  padding: ${rem(14)} ${rem(18)};
  background: var(--lux-cal-sunken);
  font-size: ${rem(13)};
`

const ARROW_STEPS: Record<string, number> = {
  ArrowLeft: -1,
  ArrowRight: 1,
  ArrowUp: -DAYS_IN_WEEK,
  ArrowDown: DAYS_IN_WEEK,
}

/** An explicit calendar locale wins; otherwise use the app locale everywhere. */
function useCalendarLocale(override?: string) {
  const intl = useIntl()
  return override ?? intl.locale
}

function useWeekdayLabels(locale: string, weekStartsOn: CalendarWeekday) {
  return useMemo(() => {
    const names = getWeekdayNames(locale)
    return Array.from({ length: DAYS_IN_WEEK }, (_unused, index) => {
      return names[WEEKDAY_ORDER[(index + weekStartsOn) % DAYS_IN_WEEK]]
    })
  }, [locale, weekStartsOn])
}

function Calendar<TDay = never, TSpan = never>(props: CalendarProps<TDay, TSpan>) {
  const {
    from,
    to,
    months: monthAllowlist,
    dayData,
    spans,
    selectionMode = 'none',
    value,
    onSelect = noop,
    minRangeLength,
    maxRangeLength,
    rangeUnit,
    allowSameDayRange,
    previewOnHover = false,
    onSelectionInvalid = noop,
    isDayDisabled,
    disabledBehaviour = 'inert',
    layout = 'scroll',
    density = 'comfortable',
    renderAhead = 1,
    scrollContainerRef,
    weekStartsOn: weekStartsOnProp,
    defaultMonth,
    focusMonth,
    onMonthChange = noop,
    onMonthVisible = noop,
    renderDay,
    renderSpan,
    renderMonthHeader,
    monthCaptions,
    previousMonthLabel: previousMonthLabelProp,
    nextMonthLabel: nextMonthLabelProp,
    title,
    headerAction,
    footnote,
    banner,
    dropTargets = false,
    label,
    dayLabel,
    locale,
    className,
  } = props

  const intl = useIntl()
  const rootRef = useRef<HTMLDivElement>(null)
  const [previewDate, setPreviewDate] = useState<CalendarDate | undefined>(undefined)
  const [focusedDate, setFocusedDate] = useState<CalendarDate>(() => clampCalendarDate(from, from, to))

  /*
   * A caller that knows which calendar this is can name the buttons after it,
   * so a reader meeting several on a page can tell them apart. The generic
   * names stand in when none is given.
   */
  const previousMonthLabel = previousMonthLabelProp ?? intl.formatMessage({
    id: 'TXxKbhV9a0XvY1GQdFrJ',
    defaultMessage: 'Previous month',
    description: 'Accessible name for the previous-month button on a paged trip planner calendar grid',
  })
  const nextMonthLabel = nextMonthLabelProp ?? intl.formatMessage({
    id: '34D_6ZbkLjktyWNFSoSS',
    defaultMessage: 'Next month',
    description: 'Accessible name for the next-month button on a paged trip planner calendar grid',
  })

  const resolvedLocale = useCalendarLocale(locale)
  const weekStartsOn = weekStartsOnProp ?? getCalendarWeekStart(resolvedLocale)
  const weekdays = useWeekdayLabels(resolvedLocale, weekStartsOn)

  const baseMonths = useMemo(
    () => buildCalendarMonths({ from, to, weekStartsOn, months: monthAllowlist }),
    [from, to, weekStartsOn, monthAllowlist],
  )

  const layoutResult = useMemo(() => buildSpanLayout(baseMonths, spans), [baseMonths, spans])
  const { months, segmentsByWeek, segmentsByDate } = layoutResult

  const anchorIndex = useMemo(() => {
    const target = defaultMonth ?? toMonthKey(from)
    const index = months.findIndex(month => month.key === target)
    return index === -1 ? 0 : index
  }, [defaultMonth, from, months])

  const [pageIndex, setPageIndex] = useState(anchorIndex)

  useEffect(() => {
    if (layout !== 'paged' || !focusMonth) return
    const index = months.findIndex(month => month.key === focusMonth)
    if (index !== -1) setPageIndex(index)
  }, [focusMonth, months, layout])

  useEffect(() => {
    if (pageIndex < months.length) return
    setPageIndex(Math.max(months.length - 1, 0))
  }, [months.length, pageIndex])

  const { visibleMonths: windowedMonths, topSentinelRef, bottomSentinelRef } = useMonthWindow(months, {
    enabled: layout === 'scroll',
    renderAhead,
    anchorIndex,
    scrollContainerRef,
  })

  const visibleMonths = layout === 'paged' ? months.slice(pageIndex, pageIndex + 1) : windowedMonths
  const pagedMonthKey = layout === 'paged' ? months[pageIndex]?.key : undefined

  useEffect(() => {
    if (pagedMonthKey) onMonthVisible(pagedMonthKey)
  }, [pagedMonthKey, onMonthVisible])

  const goToPreviousMonth = useCallback(() => {
    const next = pageIndex - 1
    if (next < 0) return
    setPageIndex(next)
    onMonthChange(months[next].key, 'backwards')
  }, [pageIndex, months, onMonthChange])

  const goToNextMonth = useCallback(() => {
    const next = pageIndex + 1
    if (next >= months.length) return
    setPageIndex(next)
    onMonthChange(months[next].key, 'forwards')
  }, [pageIndex, months, onMonthChange])

  const handleSelect = useCallback((date: CalendarDate) => {
    if (selectionMode === 'none' || !value) return

    const day = months
      .flatMap(month => month.weeks)
      .flatMap(week => week.days)
      .find(candidate => candidate.date === date && !candidate.isAdjacentMonth)
    if (!day) return

    const data = dayData?.get(date)

    if (day.isOutOfBounds) {
      onSelectionInvalid(day, 'out-of-bounds')
      return
    }
    if (isDayDisabled?.(day, data)) {
      onSelectionInvalid(day, 'disabled')
      if (disabledBehaviour === 'inert') return
    }

    const result = selectDay(value, date, { minRangeLength, maxRangeLength, rangeUnit, allowSameDayRange })
    if (result.invalidReason) {
      onSelectionInvalid(day, result.invalidReason)
      return
    }

    setFocusedDate(date)
    onSelect(result.selection, day, data)
  }, [
    selectionMode, value, months, dayData, isDayDisabled, disabledBehaviour,
    minRangeLength, maxRangeLength, rangeUnit, allowSameDayRange, onSelect, onSelectionInvalid,
  ])

  const handleHover = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!previewOnHover) return
    const target = (event.target as HTMLElement).closest('[data-calendar-date]')
    setPreviewDate(target?.getAttribute('data-calendar-date') ?? undefined)
  }, [previewOnHover])

  const handleHoverEnd = useCallback(() => {
    if (previewOnHover) setPreviewDate(undefined)
  }, [previewOnHover])

  /*
   * Arrows step from `focusedDate`, so it has to follow real focus. Without
   * this it only ever moved when the arrows or a selection wrote it, and
   * focusing a cell any other way -- a click, a tab -- left the next arrow
   * press jumping from wherever it had been last set.
   */
  const handleDayFocus = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    const date = event.target.getAttribute?.('data-calendar-date')
    if (date) setFocusedDate(date)
  }, [])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = ARROW_STEPS[event.key]
    const isPaging = event.key === 'PageUp' || event.key === 'PageDown'
    if (step === undefined && !isPaging) return

    event.preventDefault()
    const next = isPaging
      ? clampCalendarDate(addCalendarDays(focusedDate, event.key === 'PageUp' ? -28 : 28), from, to)
      : clampCalendarDate(addCalendarDays(focusedDate, step), from, to)

    setFocusedDate(next)

    if (layout === 'paged') {
      const monthIndex = months.findIndex(month => month.key === toMonthKey(next))
      if (monthIndex !== -1 && monthIndex !== pageIndex) {
        setPageIndex(monthIndex)
        onMonthChange(months[monthIndex].key, monthIndex > pageIndex ? 'forwards' : 'backwards')
      }
    }

    window.requestAnimationFrame(() => {
      rootRef.current
        ?.querySelector<HTMLElement>(`[data-calendar-date="${next}"]:not([disabled])`)
        ?.focus()
    })
  }, [focusedDate, from, to, layout, months, pageIndex, onMonthChange])

  const renderDayCell = useCallback((day: CalendarDay, laneCount: number) => {
    const data = dayData?.get(day.date)
    const disabledByConsumer = isDayDisabled?.(day, data) ?? false
    const state = resolveDayState(day, { selection: value, previewDate, isDisabled: disabledByConsumer })

    if (renderDay) {
      const slot: CalendarDaySlot<TDay, TSpan> = {
        day,
        data,
        state,
        density,
        laneCount,
        spans: segmentsByDate.get(day.date) ?? [],
        select: () => handleSelect(day.date),
      }
      return <React.Fragment key={day.date}>{renderDay(slot)}</React.Fragment>
    }

    return <CalendarDayCell
      key={day.date}
      date={day.date}
      day={day.day}
      state={state}
      isAdjacentMonth={day.isAdjacentMonth}
      isToday={day.isToday}
      isUnavailable={disabledByConsumer}
      laneCount={laneCount}
      centred={density === 'compact' && laneCount === 0}
      disabled={day.isOutOfBounds || (disabledByConsumer && disabledBehaviour === 'inert')}
      onSelect={handleSelect}
      ariaLabel={dayLabel?.(day, data)}
    />
  }, [
    dayData, isDayDisabled, value, previewDate, renderDay, density,
    segmentsByDate, handleSelect, disabledBehaviour, dayLabel,
  ])

  const renderDefaultMonthHeader = useCallback((month: CalendarMonth) => {
    const monthStart = new Date(month.year, month.month, 1)
    return <MonthLabel>{intlDateFormatter(monthStart, INTL_LONG_MONTH_LONG_YEAR, resolvedLocale)}</MonthLabel>
  }, [resolvedLocale])

  const renderMonth = useCallback((month: CalendarMonth) => {
    const header = (renderMonthHeader ?? renderDefaultMonthHeader)(month)

    return <React.Fragment key={month.key}>
      {layout === 'paged' && <CalendarMonthNav
        previousLabel={previousMonthLabel}
        nextLabel={nextMonthLabel}
        disablePrevious={pageIndex <= 0}
        disableNext={pageIndex >= months.length - 1}
        onPrevious={goToPreviousMonth}
        onNext={goToNextMonth}
      >
        {header}
      </CalendarMonthNav>}
      {layout !== 'paged' && header}
      {monthCaptions?.[month.key]}
      <Grid>
        {month.weeks.map((week, weekIndex) => {
          const segments = segmentsByWeek.get(getWeekKey(month.key, weekIndex)) ?? []
          return <Week key={week.days[0].date}>
            <Cells>{week.days.map(day => renderDayCell(day, week.laneCount))}</Cells>
            {segments.length > 0 && renderSpan && <Lanes>
              {segments.map(segment => (
                <React.Fragment key={`${segment.span.id}:${weekIndex}`}>{renderSpan(segment)}</React.Fragment>
              ))}
            </Lanes>}
          </Week>
        })}
      </Grid>
    </React.Fragment>
  }, [
    layout, renderMonthHeader, renderDefaultMonthHeader, previousMonthLabel, nextMonthLabel,
    pageIndex, months.length, goToPreviousMonth, goToNextMonth, monthCaptions,
    segmentsByWeek, renderDayCell, renderSpan,
  ])

  return <Root ref={rootRef} className={clsx(className, `density-${density}`)}>
    {(title || headerAction) && <Header>
      {title && <Title>{title}</Title>}
      {headerAction}
    </Header>}

    <WeekdayRow aria-hidden="true">
      {weekdays.map(weekday => <span key={weekday.short} title={weekday.long}>{weekday.short}</span>)}
    </WeekdayRow>

    {/*
      * `group`, not `grid`. The month renders as month > week > button with no
      * row or gridcell in between and an aria-hidden weekday header, so `grid`
      * would promise a structure a reader then cannot find -- worse than the
      * neighbouring calendars, which claim nothing. `group` carries the name
      * without the promise. Real grid semantics belong to all seven calendars
      * at once, not to this one.
      */}
    <div
      role="group"
      aria-label={label}
      data-drop-targets={dropTargets ? 'true' : undefined}
      onKeyDown={handleKeyDown}
      onFocus={handleDayFocus}
      onMouseOver={handleHover}
      onMouseLeave={handleHoverEnd}
    >
      {layout === 'scroll' && <div ref={topSentinelRef} />}
      {visibleMonths.map(renderMonth)}
      {layout === 'scroll' && <div ref={bottomSentinelRef} />}
    </div>

    {footnote && <Footnote>{footnote}</Footnote>}
    {banner && <Banner>{banner}</Banner>}
  </Root>
}

export default Calendar
